// 회원 선택 리스트 — 관리자 대리 예약 다이얼로그 공용 (연습실/레슨)
// 검색창 + 스크롤 리스트. 네이티브 select / Autocomplete 대체.
import React, { useState } from "react";
import { Box, Typography, Avatar, ButtonBase, InputBase } from "@mui/material";
import { Search, Check } from "@mui/icons-material";
import { useDarkMode } from "../App";
import { matchesAnySearch } from "../utils/hangul";

const memberTypeLabels = { COUNT: "횟수제", FIXED: "고정제", SCHEDULE: "스케줄제" };

function buildSub(m) {
  return [
    m.username,
    memberTypeLabels[m.member_type] || "회원",
    m.member_type !== "FIXED" && m.ticket != null ? `잔여 ${m.ticket}회` : null,
  ].filter(Boolean).join(" · ");
}

export default function MemberPicker({ members, selectedId, onSelect, selfOption, maxHeight = 230 }) {
  const { darkMode } = useDarkMode();
  const [search, setSearch] = useState("");

  // 초성 검색 지원 — 'ㅎㄱㄷ' 로 '홍길동' 이 잡힌다.
  const query = search.trim();
  const filtered = members.filter(m => matchesAnySearch([m.name, m.username], query));

  const rows = [
    ...(selfOption ? [{ id: selfOption.id, name: selfOption.label, sub: null, self: true, avatarChar: selfOption.label.charAt(0) }] : []),
    ...filtered.map(m => ({
      id: m.id,
      name: m.name,
      sub: buildSub(m),
      self: false,
      avatarChar: (m.name || "?").charAt(0),
    })),
  ];

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, overflow: "hidden" }}>
      <InputBase
        fullWidth
        placeholder="이름 또는 초성으로 검색"
        value={search}
        onChange={e => setSearch(e.target.value)}
        startAdornment={<Search sx={{ fontSize: 18, color: "text.disabled", mr: 1 }} />}
        sx={{ px: 1.5, py: 0.75, fontSize: "0.9rem", borderBottom: "1px solid", borderColor: "divider" }}
      />
      <Box sx={{ maxHeight, overflowY: "auto" }}>
        {rows.map(row => {
          const selected = selectedId === row.id;
          return (
            <ButtonBase
              key={row.id}
              onClick={() => onSelect(row.id)}
              sx={{
                display: "flex", width: "100%", justifyContent: "flex-start", textAlign: "left",
                alignItems: "center", gap: 1.25, px: 1.5, py: 0.9,
                bgcolor: selected ? (darkMode ? "rgba(255,255,255,0.08)" : "#FAFAF7") : "transparent",
                "&:hover": { bgcolor: darkMode ? "rgba(255,255,255,0.06)" : "rgba(17,17,17,0.04)" },
              }}
            >
              <Avatar sx={{
                width: 30, height: 30, fontSize: "0.8rem", fontWeight: 600,
                bgcolor: row.self ? (darkMode ? "grey.700" : "#F1EFE8") : "primary.main",
                color: row.self ? "text.secondary" : "primary.contrastText",
              }}>
                {row.avatarChar}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={selected ? 600 : 400} noWrap>{row.name}</Typography>
                {row.sub && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">{row.sub}</Typography>
                )}
              </Box>
              {selected && <Check sx={{ fontSize: 18, color: "secondary.main" }} />}
            </ButtonBase>
          );
        })}
        {filtered.length === 0 && query && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", py: 1.5 }}>
            검색 결과가 없습니다
          </Typography>
        )}
      </Box>
    </Box>
  );
}
